<template>
    <v-sheet>
        <v-row>
            <v-col>
                <h4 class="text-h4">Sales Scheduling</h4>
                <p class="text-subtitle-1">Sales Scheduling</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="4">
                <v-sheet elevation="2">
                    <v-date-picker full-width v-model="reportDate" no-title></v-date-picker>
                </v-sheet>
            </v-col>
            <v-col cols="12" md="8">
                <v-sheet>
                    <v-row>
                        <v-col cols="12" md="4" v-if="!isLockedDate && hasPermission('SASCHC')">
                            <v-btn block color="primary" v-if="this.authorizations.sales_scheduling.create" @click="handleEventCreate">
                                <v-icon left>mdi-plus</v-icon>
                                new schedule
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="4" v-if="hasPermission('SASCHL')">
                            <v-btn depressed block color="success--text" v-if="isLockedDate" @click="unlockDateAsync">
                                <v-icon left>mdi-lock</v-icon>
                                unlock date
                            </v-btn>
                            <v-btn depressed block color="error--text" v-else @click="lockDateAsync">
                                <v-icon left>mdi-lock</v-icon>
                                lock date
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-btn block depressed @click="onCalendarView">
                                <v-icon left>mdi-calendar</v-icon>
                                calender
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="4" @click="exportCSV" v-if="hasPermission('SASCHL')">
                            <v-btn block depressed>
                                <v-icon left>mdi-file-excel</v-icon>
                                export
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-sheet>
                                <div>
                                    <div>Total Quantity</div>
                                    <div class="text-h3">{{ formatNumber(getTotal(summary.cast_types_total, 'quantity')) }}</div>
                                </div>
                            </v-sheet>
                        </v-col>
                        <v-col>
                            <v-sheet class="d-flex flex-row">
                                <div>
                                    <div>Date Lock State</div>
                                    <div class="text-h3">
                                        <span class="error--text" v-if="isLockedDate">Locked</span>
                                        <span class="success--text" v-else>Open</span>
                                    </div>
                                </div>
                            </v-sheet>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-simple-table dense>
                                <thead>
                                    <tr>
                                        <th>Cast Type</th>
                                        <th>Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(d, i) in summary.cast_types_total" :key="i">
                                        <td>{{ d.cast_type }}</td>
                                        <td>{{ formatNumber(d.quantity) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="grey lighten-3 font-weight-bold">
                                        <td>Total</td>
                                        <td>{{ formatNumber(getTotal(summary.cast_types_total, 'quantity')) }}</td>
                                    </tr>
                                </tfoot>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-data-table
                    dense
                    disable-pagination
                    hide-default-footer
                    show-group-by
                    @dblclick:row="handleEventClick"
                    :headers="headers"
                    :items="data">

                    <template v-slot:[`item.action`]="{item}">
                        
                        <v-btn icon @click="handleEventClick(null, { item })">
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>

                    </template>

                    <template v-slot:[`item.customer`]="{item}">
                        <td>{{item.customer_code}} - {{item.customer}}</td>
                    </template>

                    <template v-slot:[`item.quantity`]="{value}">
                        <td>{{formatNumber(value, 2)}}</td>
                    </template>

                    <template v-slot:[`item.cubes`]="{value}">
                        <td>{{value > 0 ? `${value} Cubes` : "None"}}</td>
                    </template>

                    <template v-slot:[`item.start_at`]="{value}">
                        <td>{{formatTime(value)}}</td>
                    </template>

                    <template v-slot:[`item.state`]="{item}">
                        <td>
                            <v-badge inline left dot :color="renderColor(item.state_id)"></v-badge>
                            {{item.state}}
                        </td>
                    </template>

                    <template v-slot:[`foot`]="{items}">
                        <tfoot>
                            <tr class="surface">
                                <td></td>
                                <td>Total</td>
                                <td colspan="9">{{getTotal(items, "quantity", 2)}}</td>
                            </tr>
                        </tfoot>
                    </template>

                </v-data-table>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="8">
                <v-subheader>Waiting List</v-subheader>
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Customer</th>
                            <th>Quantity</th>
                            <th>Prefered Date</th>
                            <th>Schedule Date</th>
                            <th>Priority</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(d, i) in waitingList" :key="i">
                            <td>
                                <v-btn icon @click="handleEventClick(null, { item: d })">
                                    <v-icon>mdi-magnify</v-icon>
                                </v-btn>
                            </td>
                            <td>{{ d.customer_name }}</td>
                            <td>{{ formatNumber(d.quantity, 2) }}</td>
                            <td>{{ d.ready_date }}</td>
                            <td>{{ d.date }}</td>
                            <td>{{ d.priority_name }}</td>
                        </tr>
                        <tr v-if="waitingList" class="text-center">
                            <td colspan="6">No Schedules on the Waiting List</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>
        <v-overlay v-model="loading">
            <v-sheet color="white" class="pa-4" width="300" elevation="1" rounded>
                <v-progress-linear indeterminate color="primary"/>
                <div class="primary--text text-center mt-2">Please Wait ...</div>
            </v-sheet>
        </v-overlay>
        <v-snackbar v-model="snackbar.display" :color="snackbar.color" right bottom>{{snackbar.message}}</v-snackbar>
        <v-dialog v-model="eventEditShown" width="700" scrollable persistent>
            <SchedulingEventEdit
                v-if="eventEditShown"
                :event-id="selectedEvent.id"
                :date="reportDate"
                @close="eventEditShown = false"
                @fatal="eventEditShown = false"
                @success="handleEventSubmit($event, 'success')"
                @error="handleEventSubmit($event, 'error')"
                />
        </v-dialog>
        <v-dialog v-model="eventCreateDialog" width="700" scrollable persistent>
            <SchedulingEventCreate
                :date="reportDate"
                v-if="eventCreateDialog"
                @cancel="eventCreateDialog = false"
                @created="onEventCreated"
                />
        </v-dialog>
        <v-dialog v-model="eventDisplayDialog" width="1024" scrollable persistent>
            <SchedulingEventDisplay
                v-if="eventDisplayDialog"
                :event-id="selectedEvent.id"
                @close="eventDisplayDialog = false"      
                @updated="fetchSchedules"
            />
        </v-dialog>
    </v-sheet>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

import SchedulingEventEdit from './../../components/Sales/SchedulingEventEdit.vue';
import SchedulingEventDisplay from '../../components/Sales/SchedulingEventDisplay.vue';
import SchedulingEventCreate from '../../components/Sales/SchedulingEventCreate.vue';

import { getSalesSchedules, getWaitingList } from './../../services/scheduling/schedule';
import { states } from './../../data/sales/scheduling/states';
import { formatNumber } from 'accounting';
import { getTotal } from './../../utils/accounting';
import lockedDateAPI from "./../../services/scheduling/lockedDate";
import summaryAPI from "./../../services/scheduling/summary";
import authorize from '../../utils/authorize';

export default {
    name: "SchedulingReport",
    components: {
        SchedulingEventEdit,
        SchedulingEventDisplay,
        SchedulingEventCreate,
    },
    data() {
        return {
            loading: false,
            lockingDateLoading: false,
            data: [],
            waitingList:[],
            reportDate: moment().format("YYYY-MM-DD"),
            dow: "",
            snackbar: {},
            errors: [],
            eventEditShown: false,
            eventDisplayDialog: false,
            eventCreateDialog: false,
            selectedEvent: {},
            headers: [
                { text: "", value: "action", groupable: false },
                { text: "Customer", value: "customer", groupable: false},
                { text: "Item", value: "item", groupable: false},
                { text: "Quantity", value: "quantity", groupable: false},
                { text: "Cast Type", value: "cast_type", groupable: false},
                { text: "Cubes", value: "cubes", groupable: false},
                { text: "Floor", value: "floor", groupable: false},
                { text: "Site", value: "site", groupable: false},
                { text: "Plant", value: "plant"},
                { text: "State", value: "state"},
            ],
            isLockedDate: false,
            summary: {},
            exportLoading: false
        }
    },
    computed: {
        ...mapState('authentication', ["authorizations"]),
        totalQuantity() {
            return getTotal(this.data, "quantity", 2)
        }
    },
    watch: {
        reportDate(val) {
            this.fetchSchedules();
            this.dow = moment(val).format('dddd');
        },
    },
    methods: {
        hasPermission(code) {
            return authorize(code)
        },
        formatNumber,
        getTotal,
        moment,
        formatTime(time) {
            return moment(time).format("hh:mm a");
        },
        async fetchSchedules() {
            try {
                // reset data
                this.data = [];
                this.summary = {};
                this.waitingList  = [];

                this.loading = true;

                // fetch schedules
                const resp = await getSalesSchedules({start: this.reportDate, end: this.reportDate});
                this.data = resp.data;

                // fetch waiting list
                const { data: waitingList } = await getWaitingList(this.reportDate);
                this.waitingList = waitingList;

                // fetch summary
                const {data: summary} = await summaryAPI.summary(this.reportDate);
                this.summary = summary;
                this.isLockedDate = summary.is_locked_date;
                
                this.loading = false;
            } catch {
                this.loading = false;
            }
        },
        renderColor(stateId) {
            let state = states.find(each => each.id == stateId);
            return state ? state.color : "black";
        },
        handleCalendarNavigation() {
            this.$router.push({ path: "/sales/scheduling", query: { date: this.reportDate, type: "day" } });
        },
        handleEventClick(e, {item}) {
            console.log(item);
            this.selectedEvent = item;
            this.eventDisplayDialog = true;
        },
        handleEventSubmit(message, type) {
            this.fetchSchedules();
            let color = type === "success" ? "green" : "red";
            this.showAlert(message, color);
            if(type === "success") {
                this.eventEditShown = false;
            }
        },
        showAlert(message, color) {
            this.snackbar = { message, color, display: true};
        },
        handleEventCreate() {
            this.eventCreateDialog = true
        },
        lockDateAsync() {
            this.lockingDateLoading = true;
            lockedDateAPI.lock(this.reportDate).then(() => {
                this.fetchSchedules();
            }).finally(() => {
                this.lockingDateLoading = false;
            })
        },
        unlockDateAsync() {
            this.lockingDateLoading = true;
            lockedDateAPI.unlock(this.reportDate).then(() => {
                this.fetchSchedules();
            }).finally(() => {
                this.lockingDateLoading = false;
            })
        },
        onEventCreated() {
            this.eventCreateDialog = false;
            this.fetchSchedules();
        },
        onCalendarView() {
            this.$router.push({ path: "/sales/scheduling", query: { date: this.reportDate, type: "month" } });
        },
        exportCSV() {

            var csvRows = [];            

            let data = this.data.map(each => {

                // TODO: remove this cement calculation, it was done for convenience.
                // Later it should be calculated from bill of materials
                // Take the cement quantity from the item name using regex and convert it to number
                // regex should match number ending with kg
                let cementContent = 0;
                let cementRegex = /(\d+)(kg)/g;
                let cementMatch = cementRegex.exec(each.item);
                if(cementMatch) {
                    cementContent = Number(cementMatch[1]);
                }

                return {
                    "Date": moment(each.start_at).format("YYYY-MM-DD"),
                    "Code": each.customer_code,
                    "Customer": each.customer,
                    "Item": each.item,
                    "Quantity": each.quantity,
                    "floor": each.floor,
                    "Type": each.cast_type,
                    "Site": each.site,
                    "Batching Plant": each.plant,
                    "Lab": each.cubes,
                    "State": each.state,
                    "Cement/m3 (Kg)": cementContent,
                    "Cement (Ton)": formatNumber((cementContent * each.quantity) / 1000),
                }
            })
        
            const headers = Object.keys(data[0]);
            csvRows.push(`${headers.join(',')}\n`);

            data.forEach(each => {
                const values = `${Object.values(each).join(',')}\n`;
                csvRows.push(values)    
            })

            const blob = new Blob(csvRows, { type: 'application/csv' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `production plan for ${this.reportDate}.csv`
            link.click()
            URL.revokeObjectURL(link.href)
        }
    },
    created() {
        if(this.$route.query.date) {
            this.reportDate = this.$route.query.date;
        }
        this.fetchSchedules();
    }
}
</script>

<style>

</style>